import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, FileSpreadsheetIcon, CheckCircle2Icon, TableIcon, MapPinIcon, FilterIcon, UsersIcon, SearchIcon, TargetIcon, PackageIcon, RefreshCwIcon, BarChart3Icon, FileTextIcon, FileIcon, DownloadIcon, ArrowRightIcon, MailIcon, ExternalLinkIcon, ChevronRightIcon, DatabaseIcon } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, PieChart, Pie } from 'recharts';
const tableData = [{
  rank: 1,
  name: 'Supercuts',
  domain: 'supercuts.com',
  size: '50–200',
  score: 94
}, {
  rank: 2,
  name: 'Sephora',
  domain: 'sephora.com',
  size: '200+',
  score: 91
}, {
  rank: 3,
  name: 'SmartStyle',
  domain: 'smartstyle.com',
  size: '50–200',
  score: 87
}, {
  rank: 4,
  name: 'First Choice Hair',
  domain: 'firstchoicehaircutters.com',
  size: '10–50',
  score: 82
}, {
  rank: 5,
  name: 'Chatters',
  domain: 'chatters.ca',
  size: '10–50',
  score: 79
}];
const barChartData = [{
  name: 'Supercuts',
  locations: 36
}, {
  name: 'Sephora',
  locations: 11
}, {
  name: 'SmartStyle',
  locations: 10
}, {
  name: 'Great Clips',
  locations: 8
}, {
  name: 'Chatters',
  locations: 6
}, {
  name: 'First Choice',
  locations: 5
}, {
  name: 'Regis Salon',
  locations: 5
}, {
  name: 'Cost Cutters',
  locations: 3
}];
const pieData = [{
  name: 'High intent',
  value: 33
}, {
  name: 'Strong ICP fit',
  value: 25
}, {
  name: 'Revenue potential',
  value: 20
}, {
  name: 'Tech ready',
  value: 13
}, {
  name: 'Other',
  value: 9
}];
const PIE_COLORS = ['#3b82f6', '#6366f1', '#818cf8', '#93c5fd', '#cbd5e1'];
const savedFiles = [{
  name: 'scored_leads_ranked.csv',
  meta: '28 accounts · 4-dimension scoring',
  icon: FileSpreadsheetIcon,
  color: 'text-blue-500',
  bgColor: 'bg-blue-50',
  size: '32 KB'
}, {
  name: 'personalized_outreach_emails.md',
  meta: '28 personalized emails · ready to send',
  icon: MailIcon,
  color: 'text-emerald-500',
  bgColor: 'bg-emerald-50',
  size: '18 KB'
}, {
  name: 'decision_maker_contacts.csv',
  meta: 'Owner & manager contacts · verified',
  icon: FileTextIcon,
  color: 'text-violet-500',
  bgColor: 'bg-violet-50',
  size: '12 KB'
}];
const suggestions = [{
  icon: MailIcon,
  label: 'Draft follow-up sequence'
}, {
  icon: SearchIcon,
  label: 'Find similar accounts'
}, {
  icon: UsersIcon,
  label: 'Enrich more contacts'
}, {
  icon: FilterIcon,
  label: 'Adjust scoring weights'
}];
export function OpenclawDemo() {
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5330">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-5331">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-5332">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-bold mb-4 border border-blue-100" data-id="element-5333">
            <SparklesIcon className="w-4 h-4" data-id="element-5334" />
            Product output
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight" data-id="element-5335">
            Structured deliverables, ready to act on
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto" data-id="element-5336">
            The agent returns more than a list of leads. It packages the output
            into organized, usable deliverables so teams can review findings,
            prioritize accounts, and move directly into outreach.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-5337">
          {/* Prioritize Card - Full Width */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-12 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6 md:p-8" data-id="element-5338">
            <div className="flex items-center gap-4 mb-2" data-id="element-5339">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0" data-id="element-5340">
                <TargetIcon className="w-5 h-5" data-id="element-5341" />
              </div>
              <h3 className="text-xl font-bold text-slate-900" data-id="element-5342">
                Prioritize the right accounts
              </h3>
            </div>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed pl-[60px]" data-id="element-5343">
              Each lead is scored across revenue potential, intent to buy, ICP
              match, and tech readiness. Based on your priorities, we assign
              different weights to each dimension so your highest-value leads
              always surface first.
            </p>

            {/* Horizontal layout: Table + Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5" data-id="element-5344">
              {/* Chat-style Table */}
              <div className="lg:col-span-7 bg-slate-50 rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-id="element-5345">
                <div className="p-5 flex-1" data-id="element-5346">
                  <div className="flex items-center gap-2 text-xs text-stone-500 pl-1 mb-3" data-id="element-5347">
                    <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-5348" />
                    Ran 3 commands, scored 28 companies &gt;
                  </div>
                  <div className="flex items-start gap-3" data-id="element-5349">
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-5350">
                      <SparklesIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-5351" />
                    </div>
                    <div className="flex-1 space-y-3" data-id="element-5352">
                      <p className="text-sm text-stone-800" data-id="element-5353">
                        I found <strong data-id="element-5354">28 qualified leads</strong>. Here are
                        the top results scored by revenue potential and intent:
                      </p>
                      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden" data-id="element-5355">
                        <div className="overflow-x-auto" data-id="element-5356">
                          <table className="w-full text-sm" data-id="element-5357">
                            <thead data-id="element-5358">
                              <tr className="bg-stone-50/50 text-left border-b border-stone-100" data-id="element-5359">
                                <th className="px-4 py-3 text-[11px] font-semibold text-stone-500 w-10" data-id="element-5360">
                                  #
                                </th>
                                <th className="px-4 py-3 text-[11px] font-semibold text-stone-500" data-id="element-5361">
                                  Company
                                </th>
                                <th className="px-4 py-3 text-[11px] font-semibold text-stone-500" data-id="element-5362">
                                  Domain
                                </th>
                                <th className="px-4 py-3 text-[11px] font-semibold text-stone-500" data-id="element-5363">
                                  Size
                                </th>
                                <th className="px-4 py-3 text-[11px] font-semibold text-stone-500 text-right" data-id="element-5364">
                                  Score
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-50" data-id="element-5365">
                              {tableData.map((row, idx) => <tr key={row.rank} className="hover:bg-stone-50/50 transition-colors" data-id="element-5366">
                                  <td className="px-4 py-3.5 text-[12px] text-stone-400 font-medium" data-id="element-5367">
                                    {row.rank}
                                  </td>
                                  <td className="px-4 py-3.5 font-bold text-stone-900" data-id="element-5368">
                                    {row.name}
                                  </td>
                                  <td className="px-4 py-3.5" data-id="element-5369">
                                    <div className="flex items-center gap-1.5 text-[12px] text-stone-500" data-id="element-5370">
                                      {row.domain}
                                      <ExternalLinkIcon className="w-3 h-3 opacity-50" data-id="element-5371" />
                                    </div>
                                  </td>
                                  <td className="px-4 py-3.5 text-[12px] text-stone-600" data-id="element-5372">
                                    {row.size}
                                  </td>
                                  <td className="px-4 py-3.5 text-right" data-id="element-5373">
                                    <span className={`inline-flex items-center justify-center px-3 py-1.5 rounded-xl text-sm font-bold ${row.score >= 90 ? 'bg-blue-100 text-blue-700' : 'bg-blue-50 text-blue-600'}`} data-id="element-5374">
                                      {row.score}
                                    </span>
                                  </td>
                                </tr>)}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts Column */}
              <div className="lg:col-span-5 flex flex-col gap-4" data-id="element-5375">
                {/* Bar Chart */}
                <div className="flex-1 bg-slate-50 rounded-xl border border-slate-200 p-5 flex flex-col" data-id="element-5376">
                  <div className="flex items-center gap-2 mb-3" data-id="element-5377">
                    <BarChart3Icon className="w-4 h-4 text-blue-500" data-id="element-5378" />
                    <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider" data-id="element-5379">
                      Location count by chain
                    </div>
                  </div>
                  <div className="flex-1 w-full min-h-0" data-id="element-5380">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5381">
                      <BarChart data={barChartData} margin={{
                      top: 5,
                      right: 5,
                      left: -25,
                      bottom: 0
                    }} data-id="element-5382">
                        <XAxis dataKey="name" tick={{
                        fontSize: 9,
                        fill: '#64748b'
                      }} tickLine={false} axisLine={false} tickFormatter={value => value.length > 8 ? `${value.substring(0, 8)}…` : value} data-id="element-5383" />
                        <YAxis tick={{
                        fontSize: 9,
                        fill: '#94a3b8'
                      }} tickLine={false} axisLine={false} data-id="element-5384" />
                        <Tooltip cursor={{
                        fill: '#f1f5f9'
                      }} contentStyle={{
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        fontSize: '11px'
                      }} itemStyle={{
                        color: '#0f172a',
                        fontWeight: 600
                      }} labelStyle={{
                        color: '#64748b',
                        fontSize: '10px',
                        marginBottom: '4px'
                      }} data-id="element-5385" />
                        <Bar dataKey="locations" radius={[5, 5, 0, 0]} maxBarSize={28} data-id="element-5386">
                          {barChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={index === 0 ? '#3b82f6' : '#93c5fd'} data-id="element-5387" />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Pie Chart */}
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-5" data-id="element-5388">
                  <div className="flex items-center gap-2 mb-3" data-id="element-5389">
                    <TargetIcon className="w-4 h-4 text-indigo-500" data-id="element-5390" />
                    <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider" data-id="element-5391">
                      Score dimension breakdown
                    </div>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-5392">
                    <div className="w-[110px] h-[110px] shrink-0" data-id="element-5393">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-5394">
                        <PieChart data-id="element-5395">
                          <Pie data={pieData} cx="50%" cy="50%" innerRadius={28} outerRadius={50} paddingAngle={3} dataKey="value" stroke="none" data-id="element-5396">
                            {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} data-id="element-5397" />)}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex-1 space-y-2" data-id="element-5398">
                      {pieData.map((d, i) => <div key={i} className="flex items-center justify-between" data-id="element-5399">
                          <div className="flex items-center gap-2" data-id="element-5400">
                            <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{
                          backgroundColor: PIE_COLORS[i]
                        }} data-id="element-5401" />
                            <span className="text-[11px] text-slate-600 font-medium" data-id="element-5402">
                              {d.name}
                            </span>
                          </div>
                          <span className="text-[11px] font-bold text-slate-700" data-id="element-5403">
                            {d.value}%
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Deliverables Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-6 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6 md:p-8 flex flex-col" data-id="element-5404">
            <div className="flex items-center gap-4 mb-2" data-id="element-5405">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-5406">
                <PackageIcon className="w-5 h-5" data-id="element-5407" />
              </div>
              <h3 className="text-xl font-bold text-slate-900" data-id="element-5408">
                Get usable deliverables
              </h3>
            </div>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed pl-[60px]" data-id="element-5409">
              Structured files ready to review, share, or import into other
              tools.
            </p>

            <div className="flex-1 bg-slate-50 rounded-2xl border border-slate-200 p-6" data-id="element-5410">
              <div className="flex items-center gap-2 text-xs text-stone-500 pl-1 mb-4" data-id="element-5411">
                <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-5412" />
                Generated 3 deliverables &gt;
              </div>
              <div className="flex items-start gap-3 mb-4" data-id="element-5413">
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-5414">
                  <SparklesIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-5415" />
                </div>
                <p className="text-sm text-stone-800" data-id="element-5416">
                  All done! I've packaged the results into the following files:
                </p>
              </div>
              <div className="space-y-2.5" data-id="element-5417">
                {savedFiles.map((file, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -8
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.3,
                delay: 0.15 + i * 0.08
              }} className="flex items-center gap-3 p-3 rounded-xl border border-stone-200 bg-white shadow-sm group" data-id="element-5418">
                    <div className={`w-10 h-10 rounded-lg ${file.bgColor} flex items-center justify-center shrink-0`} data-id="element-5419">
                      <file.icon className={`w-5 h-5 ${file.color}`} data-id="element-5420" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-5421">
                      <div className="text-sm font-semibold text-stone-800 truncate" data-id="element-5422">
                        {file.name}
                      </div>
                      <div className="text-xs text-stone-500" data-id="element-5423">{file.meta}</div>
                    </div>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 text-xs font-semibold text-stone-700 hover:bg-stone-50 transition-colors opacity-0 group-hover:opacity-100" data-id="element-5424">
                      <DatabaseIcon className="w-3.5 h-3.5 text-green-600" data-id="element-5425" />
                      Drive
                    </button>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>

          {/* Continue Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="lg:col-span-6 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6 md:p-8 flex flex-col" data-id="element-5426">
            <div className="flex items-center gap-4 mb-2" data-id="element-5427">
              <div className="w-11 h-11 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0" data-id="element-5428">
                <RefreshCwIcon className="w-5 h-5" data-id="element-5429" />
              </div>
              <h3 className="text-xl font-bold text-slate-900" data-id="element-5430">
                Continue the workflow
              </h3>
            </div>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed pl-[60px]" data-id="element-5431">
              Pick up where you left off. Extend any workflow without starting
              over.
            </p>

            <div className="flex-1 flex flex-col" data-id="element-5432">
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-5 flex-1 flex flex-col" data-id="element-5433">
                {/* Agent message */}
                <div className="flex items-start gap-3 mb-4" data-id="element-5434">
                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-5435">
                    <SparklesIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-5436" />
                  </div>
                  <div className="flex-1" data-id="element-5437">
                    <p className="text-sm text-stone-800 leading-relaxed" data-id="element-5438">
                      Here are some next steps I can help with:
                    </p>
                  </div>
                </div>

                {/* Suggestion chips - 2 column grid */}
                <div className="grid grid-cols-2 gap-2.5 pl-10 flex-1 content-start" data-id="element-5439">
                  {suggestions.map((s, i) => <motion.button key={i} initial={{
                  opacity: 0,
                  y: 6
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.25,
                  delay: 0.25 + i * 0.06
                }} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-stone-200 bg-white hover:border-blue-200 hover:bg-blue-50 transition-all text-left group shadow-sm" data-id="element-5440">
                      <div className="w-7 h-7 rounded-lg bg-stone-50 group-hover:bg-white border border-stone-100 flex items-center justify-center shrink-0" data-id="element-5441">
                        <s.icon className="w-3.5 h-3.5 text-stone-500 group-hover:text-blue-600" data-id="element-5442" />
                      </div>
                      <span className="text-[12px] font-medium text-stone-700 group-hover:text-blue-700" data-id="element-5443">
                        {s.label}
                      </span>
                    </motion.button>)}
                </div>

                {/* Input hint */}
                <div className="mt-4 pl-10" data-id="element-5444">
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-stone-200 bg-white shadow-sm" data-id="element-5445">
                    <span className="text-sm text-stone-400" data-id="element-5446">
                      Ask a follow-up question...
                    </span>
                    <div className="ml-auto flex gap-0.5" data-id="element-5447">
                      <div className="w-1 h-1 rounded-full bg-stone-300 animate-pulse" data-id="element-5448" />
                      <div className="w-1 h-1 rounded-full bg-stone-300 animate-pulse" style={{
                      animationDelay: '0.2s'
                    }} data-id="element-5449" />
                      <div className="w-1 h-1 rounded-full bg-stone-300 animate-pulse" style={{
                      animationDelay: '0.4s'
                    }} data-id="element-5450" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}